import {
    Box,
    Button,
    Typography,
    Paper,
    Table,
    TableBody,
    TableRow,
    TableCell,
    TableHead,
    TextField,
    IconButton,
    Select,
    MenuItem,
    FormControl,
    Checkbox,
    FormControlLabel,
    Grid,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import React, { useState, useEffect } from 'react';

/**
 * Enhanced Fields component that can handle both dynamic form fields and static table data
 * with header rows and total rows
 * 
 * @param {Object} props - Component props
 * @param {Object} props.formik - Formik instance
 * @param {String} props.fieldArrayName - Name of the field array in formik.values
 * @param {Array} props.fields - Array of field configurations
 * @param {Object} props.options - Additional options for the component
 * @param {String} props.parentFieldPath - For internal use to track nested field paths
 * @param {Boolean} props.isObject - Indicates if the field is an object instead of an array
 * @param {Boolean} props.staticTable - Indicates if the table is static (non-editable)
 * @param {Array} props.headerColumns - Column headers for the table
 * @param {Array} props.rows - Static rows data for the table (when staticTable is true)
 * @param {Function} props.calculateTotal - Function to calculate totals for the table
 * @param {React.Component} props.subComponent - Optional component to render below the table
 */
const Fields = ({
    formik,
    fieldArrayName,
    fields = [],
    options = {},
    parentFieldPath = '',
    isObject = false,
    subComponent,
    staticTable = false,
    headerColumns = [],
    rows = [],
    calculateTotal,
}) => {
    const [editingIndex, setEditingIndex] = useState(null);
    const [editingField, setEditingField] = useState(null);
    const [customSelectValue, setCustomSelectValue] = useState(''); // State for custom select input

    // Default options
    const {
        paperProps = {},
        tableProps = { size: "small" },
        minItems = 1,
        maxItems = isObject ? 1 : Infinity,
        title = "",
        addButtonText = "Add Item",
        emptyText = "No items added",
        showAddButtonInRow = true,
        nestedLevel = 0,
        className = "dynamic-repeater-table",
        showTotals = false,
        dynamicPetFees = false // Option to enable dynamic pet fee calculation
    } = options;

    // Create the full path for accessing nested fields in formik
    const fullFieldPath = parentFieldPath
        ? `${parentFieldPath}.${fieldArrayName}`
        : fieldArrayName;

    // Access formik values safely using the field path
    const getNestedValue = (obj, path) => {
        return path.split('.').reduce((prev, curr) => {
            return prev && prev[curr] !== undefined ? prev[curr] : undefined;
        }, obj);
    };

    // Handle different data structures (array vs. object)
    let formikValues = getNestedValue(formik.values, fullFieldPath);

    // For objects, convert to an array with a single item
    if (isObject && formikValues) {
        formikValues = [formikValues];
    } else if (isObject && !formikValues) {
        formikValues = [];
    } else if (!formikValues) {
        formikValues = [];
    }

    const isEmpty = formikValues.length === 0;

    // Create a new empty item with default values from field config
    const createEmptyItem = () => {
        const emptyItem = {};
        fields.forEach(field => {
            if (field.type === 'group') {
                const groupItem = {};
                field.fields.forEach(subField => {
                    groupItem[subField.name] = subField.defaultValue !== undefined ? subField.defaultValue : '';
                });
                emptyItem[field.name] = groupItem;
            } else if (field.type === 'nestedRepeater') {
                // Initialize nested repeater as an empty array
                emptyItem[field.name] = [];
            } else {
                emptyItem[field.name] = field.defaultValue !== undefined ? field.defaultValue : '';
            }
        });
        return emptyItem;
    };

    // Set formik value with the correct path
    const setNestedFieldValue = (path, value) => {
        formik.setFieldValue(path, value);
    };

    // Handle add item
    const handleAddItem = () => {
        if (formikValues.length >= maxItems) return;

        if (isObject) {
            // For objects, we directly set the value rather than adding to an array
            setNestedFieldValue(fullFieldPath, createEmptyItem());
        } else {
            const updatedItems = [...formikValues];
            updatedItems.push(createEmptyItem());
            setNestedFieldValue(fullFieldPath, updatedItems);
        }

        // Set editing to the new item
        setEditingIndex(isObject ? 0 : formikValues.length);

        // Find the first non-nested field to edit
        const firstEditableField = fields.find(f => f.type !== 'nestedRepeater');
        if (firstEditableField) {
            if (firstEditableField.type === 'group') {
                setEditingField(`${firstEditableField.name}.${firstEditableField.fields[0].name}`);
            } else {
                setEditingField(firstEditableField.name);
            }
        }
    };

    // Handle remove item
    const handleRemoveItem = (index) => {
        if (isObject) {
            // For objects, we set to an empty object or reset values
            setNestedFieldValue(fullFieldPath, {});
            setEditingIndex(null);
            setEditingField(null);
        } else {
            const updatedItems = [...formikValues];
            updatedItems.splice(index, 1);
            setNestedFieldValue(fullFieldPath, updatedItems);

            // Reset editing index if we were editing the removed item
            if (editingIndex === index) {
                setEditingIndex(null);
                setEditingField(null);
            } else if (editingIndex > index) {
                // Adjust editing index if we were editing an item after the removed one
                setEditingIndex(editingIndex - 1);
            }
        }
    };

    // Get pet fee based on category
    const getPetFeeForCategory = (category) => {
        if (!category) return '';

        const petFees = getNestedValue(formik.values, 'property_details.pet_fees');
        if (!petFees) return '';

        switch (category) {
            case 'dog':
                return petFees.dog_fee || '';
            case 'cat':
                return petFees.cat_fee || '';
            case 'other':
                return petFees.other_fee || '';
            default:
                return '';
        }
    };

    // Improved customOnChange handling with context
    const executeCustomOnChange = (field, value, index, fieldPath, existingContext = null) => {
        if (!field || !field.customOnChange) return;

        // Use the existing context if provided or build a new one
        const context = existingContext || {
            formik,
            fieldPath,
            fieldName: field.name,
            parentPath: fullFieldPath,
            index,
            isObject,
            item: isObject ? formikValues[0] : formikValues[index],
            allItems: formikValues,
            value,
            setValue: (newValue) => {
                formik.setFieldValue(fieldPath, newValue);
            },
            setItemValue: (itemFieldName, newValue) => {
                let itemPath;
                if (isObject) {
                    itemPath = `${fullFieldPath}.${itemFieldName}`;
                } else {
                    itemPath = `${fullFieldPath}[${index}].${itemFieldName}`;
                }
                formik.setFieldValue(itemPath, newValue);
            },
            getFieldValue: (path) => getNestedValue(formik.values, path)
        };

        // Execute the custom change handler with the context
        field.customOnChange(value, context);
    };

    // Check visibility condition for a field
    const checkFieldVisibility = (field, index) => {
        // If no visibility condition, field is always visible
        if (!field.visibleIf) return true;

        try {
            // Call the visibleIf function with the current formik values
            return field.visibleIf(formik.values);
        } catch (error) {
            console.error("Error evaluating visibleIf condition:", error);
            return true; // Default to visible on error
        }
    };

    // Check group field visibility - a group is visible if any of its fields is visible
    const checkGroupVisibility = (field, index) => {
        // If the group itself has a visibility condition, check that first
        if (field.visibleIf) {
            try {
                return field.visibleIf(formik.values);
            } catch (error) {
                console.error("Error evaluating group visibleIf condition:", error);
                return true;
            }
        }

        // If no group visibility condition, check if any fields are visible
        if (field.fields && field.fields.length > 0) {
            return field.fields.some(subField => {
                if (subField.visibleIf) {
                    try {
                        return subField.visibleIf(formik.values);
                    } catch (error) {
                        console.error("Error evaluating subfield visibleIf condition:", error);
                        return true;
                    }
                }
                return true; // Subfield has no condition, so it's visible
            });
        }

        // Default case - group is visible
        return true;
    };

    // Handle field value change
    const handleFieldChange = (index, fieldName, value, field) => {
        // Save the current path for this field
        let fieldPath;
        if (isObject) {
            // For objects, we construct the path differently
            if (fieldName.includes('.')) {
                const [groupName, subFieldName] = fieldName.split('.');
                fieldPath = `${fullFieldPath}.${groupName}.${subFieldName}`;
            } else {
                fieldPath = `${fullFieldPath}.${fieldName}`;
            }
        } else {
            // For arrays, we use the index
            if (fieldName.includes('.')) {
                const [groupName, subFieldName] = fieldName.split('.');
                fieldPath = `${fullFieldPath}[${index}].${groupName}.${subFieldName}`;
            } else {
                fieldPath = `${fullFieldPath}[${index}].${fieldName}`;
            }
        }

        // Set the field value with the Promise API to ensure we can chain updates
        const updatePromise = new Promise(resolve => {
            formik.setFieldValue(fieldPath, value);
            resolve();
        });

        // Process custom onChange - for regular fields
        updatePromise.then(() => {
            if (field && field.customOnChange) {
                const context = {
                    formik,
                    fieldPath,
                    fieldName: field.name,
                    parentPath: fullFieldPath,
                    index,
                    isObject,
                    item: isObject ? formikValues[0] : formikValues[index],
                    allItems: formikValues,
                    value,
                    setValue: (newValue) => {
                        formik.setFieldValue(fieldPath, newValue);
                    },
                    setItemValue: (itemFieldName, newValue) => {
                        let itemPath;
                        if (isObject) {
                            itemPath = `${fullFieldPath}.${itemFieldName}`;
                        } else {
                            itemPath = `${fullFieldPath}[${index}].${itemFieldName}`;
                        }
                        formik.setFieldValue(itemPath, newValue);
                    },
                    getFieldValue: (path) => getNestedValue(formik.values, path)
                };
                executeCustomOnChange(field, value, index, fieldPath, context);
            }

            // For group fields, we also need to find if the group has customOnChange
            if (fieldName.includes('.')) {
                const [groupName, subFieldName] = fieldName.split('.');
                const groupField = fields.find(f => f.name === groupName && f.type === 'group');

                if (groupField && groupField.customOnChange) {
                    // For group customOnChange, we pass the entire group value
                    const groupValue = isObject
                        ? formikValues[0][groupName]
                        : formikValues[index][groupName];

                    const groupPath = isObject
                        ? `${fullFieldPath}.${groupName}`
                        : `${fullFieldPath}[${index}].${groupName}`;

                    executeCustomOnChange(groupField, groupValue, index, groupPath);
                }
            }
        });
    };

    // Get field value (handles nested fields)
    const getFieldValue = (item, fieldName) => {
        if (fieldName.includes('.')) {
            const [groupName, subFieldName] = fieldName.split('.');
            return item[groupName] ? item[groupName][subFieldName] : '';
        }
        return item[fieldName];
    };

    // Start editing a field
    const startEditing = (index, fieldName) => {
        setEditingIndex(index);
        setEditingField(fieldName);

        // Reset custom select value when starting to edit
        setCustomSelectValue('');
    };

    // Stop editing
    const stopEditing = () => {
        setEditingIndex(null);
        setEditingField(null);
        setCustomSelectValue(''); // Reset custom value when stopping edit
    };

    // Check if a field is currently being edited
    const isFieldEditing = (index, fieldName) => {
        return editingIndex === index && editingField === fieldName;
    };

    // Handle custom input for select field
    const handleCustomSelectInput = (e) => {
        setCustomSelectValue(e.target.value);
    };

    // Handle select change with support for custom values
    const handleSelectChange = (index, fieldName, value, field) => {
        // Regular select option was chosen
        handleFieldChange(index, fieldName, value, field);
        setCustomSelectValue(''); // Reset custom input
    };

    // Submit custom value from select input
    const submitCustomSelectValue = (index, fieldName, field) => {
        if (customSelectValue) {
            handleFieldChange(index, fieldName, customSelectValue, field);
            stopEditing();
        }
    };

    // Helper function to check if date should be disabled
    const shouldDisableDate = (date, field) => {
        // If blockPastDates is true, disable all dates before today
        if (field.blockPastDates === true) {
            const today = new Date();
            today.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison
            return date < today;
        }

        // If blockPastDates is a custom function, use it
        if (typeof field.blockPastDates === 'function') {
            return field.blockPastDates(date);
        }

        // Default: no dates are disabled
        return false;
    };

    // Render the appropriate input field based on field type
    const renderFieldInput = (field, index, item, isEditing) => {
        if (field.type === 'group') {
            // Check group visibility
            if (!checkGroupVisibility(field, index)) return null;

            return (
                <Grid container spacing={2}>
                    {field.fields.map((subField, subIndex) => {
                        const fullFieldName = field.name ? `${field.name}.${subField.name}` : subField.name;
                        const subFieldIsEditing = isFieldEditing(index, fullFieldName);

                        // Check subfield visibility
                        if (subField.visibleIf && !checkFieldVisibility(subField, index)) {
                            return null;
                        }

                        return (
                            <Grid item xs={12 / field.fields.length} key={subIndex}>
                                {renderSingleFieldInput(
                                    subField,
                                    index,
                                    item,
                                    subFieldIsEditing,
                                    fullFieldName,
                                    getFieldValue(item, fullFieldName)
                                )}
                            </Grid>
                        );
                    })}
                </Grid>
            );
        } else if (field.type === 'nestedRepeater') {
            // Check visibility for nested repeater
            if (!checkFieldVisibility(field, index)) return null;

            // For nested repeaters, we render another Fields component
            let nestedFieldPath;
            if (isObject) {
                nestedFieldPath = fullFieldPath;
            } else {
                nestedFieldPath = `${fullFieldPath}[${index}]`;
            }

            // Pass the dynamicPetFees flag to nested repeaters if this is the pets field
            const nestedOptions = {
                ...field.options,
                nestedLevel: nestedLevel + 1,
                dynamicPetFees: field.name === 'pets' ? true : field.options?.dynamicPetFees,
                paperProps: {
                    ...field.options?.paperProps,
                    sx: {
                        ...(field.options?.paperProps?.sx || {}),
                        borderColor: nestedLevel > 0 ? 'primary.light' : undefined
                    }
                }
            };

            return (
                <Box sx={{ mt: 1, mb: 1, pl: nestedLevel > 0 ? 2 : 0 }}>
                    <Fields
                        formik={formik}
                        fieldArrayName={field.name}
                        fields={field.fields}
                        options={nestedOptions}
                        parentFieldPath={nestedFieldPath}
                        isObject={false} // Nested repeaters are always arrays
                    />
                </Box>
            );
        } else {
            // Check visibility for regular field
            if (!checkFieldVisibility(field, index)) return null;

            // For the fee_per_pet field, we might want to disable it if it's dynamically set
            const isDisabled = dynamicPetFees && field.name === 'fee_per_pet';

            return renderSingleFieldInput(
                field,
                index,
                item,
                isEditing,
                field.name,
                item[field.name],
                isDisabled
            );
        }
    };

    // Render a single field input
    const renderSingleFieldInput = (field, index, item, isEditing, fieldName, fieldValue, isDisabled = false) => {
        // Check if field is read-only
        const isReadOnly = field.readOnly === true;
        const effectivelyDisabled = isDisabled || isReadOnly;

        if (isEditing && !isReadOnly) { // Don't allow editing if read-only
            switch (field.type) {
                case 'select':
                    // Select field implementation remains the same
                    const allowCustomValues = field.allowCustomValues === true;

                    return (
                        <FormControl fullWidth variant="standard">
                            <Select
                                value={fieldValue || ''}
                                onChange={(e) => {
                                    const newValue = e.target.value;
                                    handleSelectChange(index, fieldName, newValue, field);
                                }}
                                onBlur={!allowCustomValues ? stopEditing : undefined}
                                autoFocus
                                {...(allowCustomValues && {
                                    renderValue: (value) => {
                                        const option = field.options.find(opt => opt.value === value);
                                        return option ? option.label : value;
                                    }
                                })}
                            >
                                {field.options.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}

                                {allowCustomValues && (
                                    <Box sx={{ p: 2, borderTop: '1px solid #ddd' }}>
                                        <Typography variant="caption" display="block" gutterBottom>
                                            Or enter custom value:
                                        </Typography>
                                        <Box sx={{ display: 'flex' }}>
                                            <TextField
                                                size="small"
                                                fullWidth
                                                variant="outlined"
                                                value={customSelectValue}
                                                onChange={handleCustomSelectInput}
                                                onClick={(e) => e.stopPropagation()}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        submitCustomSelectValue(index, fieldName, field);
                                                    }
                                                }}
                                                autoFocus
                                            />
                                            <Button
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    submitCustomSelectValue(index, fieldName, field);
                                                }}
                                                sx={{ ml: 1 }}
                                            >
                                                Add
                                            </Button>
                                        </Box>
                                    </Box>
                                )}
                            </Select>
                        </FormControl>
                    );

                case 'checkbox':
                    return (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={!!fieldValue}
                                    onChange={(e) => handleFieldChange(index, fieldName, e.target.checked, field)}
                                    onBlur={stopEditing}
                                    autoFocus
                                />
                            }
                            label={field.label}
                        />
                    );

                case 'date':
                    const parseDate = (dateString) => {
                        if (!dateString) return null;
                        const [year, month, day] = dateString.split('-').map(Number);
                        if (!year || !month || !day) return null;
                        const date = new Date(year, month - 1, day);
                        return isNaN(date.getTime()) ? null : date;
                    };

                    const dateValue = parseDate(fieldValue);

                    const handleDateChange = (newDate) => {
                        if (newDate) {
                            const year = newDate.getFullYear();
                            const month = String(newDate.getMonth() + 1).padStart(2, '0');
                            const day = String(newDate.getDate()).padStart(2, '0');
                            const formattedDate = `${year}-${month}-${day}`;
                            handleFieldChange(index, fieldName, formattedDate, field);
                            stopEditing();
                        } else {
                            handleFieldChange(index, fieldName, null, field);
                        }
                    };

                    return (
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                label={field.label || "Select Date"}
                                value={dateValue}
                                onChange={handleDateChange}
                                shouldDisableDate={(date) => shouldDisableDate(date, field)}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                    }
                                }}
                                format="MM/dd/yyyy"
                            />
                        </LocalizationProvider>
                    );
                case 'text':
                default:
                    return (
                        <TextField
                            fullWidth
                            variant="standard"
                            className="p-0"
                            value={fieldValue || ''}
                            onChange={(e) => handleFieldChange(index, fieldName, e.target.value, field)}
                            onBlur={stopEditing}
                            autoFocus
                            type={field.inputType || 'text'}
                            disabled={effectivelyDisabled}
                        />
                    );
            }
        } else {
            // Display value when not editing or when read-only
            return (
                <Box
                    onClick={() => !effectivelyDisabled && startEditing(index, fieldName)}
                    sx={{
                        cursor: effectivelyDisabled ? "default" : "pointer",
                        minHeight: "36px",
                        display: "flex",
                        alignItems: "center",
                        flexGrow: 1,
                    }}
                >
                    {renderDisplayValue(field, fieldValue)}
                    {!effectivelyDisabled && (
                        <IconButton size="small" onClick={(e) => {
                            e.stopPropagation();
                            startEditing(index, fieldName);
                        }} sx={{ ml: 1 }}>
                            <EditIcon fontSize="small" color='error' />                        </IconButton>
                    )}
                </Box>
            );
        }
    };

    // Render the display value based on field type
    const renderDisplayValue = (field, value) => {
        if (value === '' || value === undefined || value === null) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', color: "#aaa" }}>
                    <span>Click to add {field.label || field.name}</span>
                </Box>
            );
        }

        switch (field.type) {
            case 'select':
                const option = field.options.find(opt => opt.value === value);
                return option ? option.label : value;

            case 'checkbox':
                return value ? 'Yes' : 'No';

            case 'date':
                if (!value) return '';
                try {
                    // Format date for display - using MM/DD/YYYY format
                    const date = new Date(value);
                    // Check if date is valid before formatting
                    if (isNaN(date.getTime())) return value;
                    return date.toLocaleDateString('en-US', {
                        month: '2-digit',
                        day: '2-digit',
                        year: 'numeric'
                    });
                } catch (e) {
                    return value;
                }

            default:
                return value;
        }
    };

    // Render the action buttons (Add/Delete) consistently across the component
    const renderActionButtons = (index) => {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', ml: 2 }}>
                {formikValues.length > minItems && (
                    <IconButton
                        size="small"
                        onClick={() => handleRemoveItem(index)}
                        color="error"
                        sx={{ mr: 1 }}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                )}

                {formikValues.length < maxItems && showAddButtonInRow && (
                    <IconButton
                        size="small"
                        onClick={handleAddItem}
                        color="primary"
                    >
                        <AddIcon fontSize="small" />
                    </IconButton>
                )}
            </Box>
        );
    };

    // Create table rows for each field within an item
    const createItemRows = (item, index) => {
        return fields.map((field, fieldIndex) => {
            // Check visibility for the field or group
            const isVisible = field.type === 'group'
                ? checkGroupVisibility(field, index)
                : checkFieldVisibility(field, index);

            // Skip rendering if not visible
            if (!isVisible) return null;

            if (field.type === 'nestedRepeater') {
                // Nested repeaters get their own section with header
                return (
                    <TableRow key={fieldIndex}>
                        <TableCell colSpan={2} sx={{ py: 1, borderBottom: '1px solid #eee' }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                {field.label || field.name}
                            </Typography>
                            {renderFieldInput(field, index, item, false)}

                            {/* Action buttons shown after nested repeater only on first field */}
                            {fieldIndex === 0 && !isObject && (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                                    {renderActionButtons(index)}
                                </Box>
                            )}
                        </TableCell>
                    </TableRow>
                );
            } else if (field.type === 'group') {
                // For group fields we create a single row with combined labels
                return (
                    <TableRow key={fieldIndex}>
                        <TableCell className="label">
                            {field.groupLabel || field.fields.map(f => f.label || f.name).join(', ')}
                        </TableCell>
                        <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                {renderFieldInput(field, index, item, false)}

                                {/* Action buttons shown only on the first row */}
                                {fieldIndex === 0 && !isObject && renderActionButtons(index)}
                            </Box>
                        </TableCell>
                    </TableRow>
                );
            } else {
                return (
                    <TableRow key={fieldIndex}>
                        <TableCell className="label">{field.label || field.name}</TableCell>
                        <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                {renderFieldInput(field, index, item, isFieldEditing(index, field.name))}

                                {/* Action buttons shown only on the first row and not for objects */}
                                {fieldIndex === 0 && !isObject && renderActionButtons(index)}
                            </Box>
                        </TableCell>
                    </TableRow>
                );
            }
        }).filter(Boolean); // Filter out null rows (from invisible fields)
    };

    // Initialize with at least one item if required and none exist
    useEffect(() => {
        // Only initialize if required minimum items > 0, currently empty, and not a nested field
        if (formikValues.length < minItems && isEmpty && !parentFieldPath) {
            if (isObject) {
                setNestedFieldValue(fullFieldPath, createEmptyItem());
            } else {
                setNestedFieldValue(fullFieldPath, [createEmptyItem()]);
            }
        }
    }, [formikValues, minItems, isEmpty, parentFieldPath, isObject, fullFieldPath]);

    // Effect to handle dynamic updating of fee_per_pet when field values change
    useEffect(() => {
        if (dynamicPetFees && formikValues && formikValues.length > 0) {
            const updatedItems = [...formikValues];
            let hasChanges = false;

            // Update each pet's fee based on its category
            updatedItems.forEach((item, idx) => {
                if (item.category) {
                    const currentFee = item.fee_per_pet;
                    const expectedFee = getPetFeeForCategory(item.category);

                    // Only update if the fee doesn't match what's expected
                    if (currentFee !== expectedFee) {
                        item.fee_per_pet = expectedFee;
                        hasChanges = true;
                    }
                }
            });

            // Only update formik if changes were made
            if (hasChanges) {
                setNestedFieldValue(fullFieldPath, updatedItems);
            }
        }
    }, [formik.values.property_details?.pet_fees, dynamicPetFees, formikValues, fullFieldPath]);

    // Effect to execute customOnChange for newly added nested repeaters
    useEffect(() => {
        if (formikValues && formikValues.length > 0) {
            formikValues.forEach((item, index) => {
                // Find nested repeater fields and check if they need customOnChange execution
                fields.forEach(field => {
                    if (field.type === 'nestedRepeater' && field.customOnChange) {
                        const nestedValue = item[field.name] || [];
                        const fieldPath = isObject
                            ? `${fullFieldPath}.${field.name}`
                            : `${fullFieldPath}[${index}].${field.name}`;

                        // Execute customOnChange for the nested repeater field
                        executeCustomOnChange(field, nestedValue, index, fieldPath);
                    }
                });
            });
        }
    }, [formikValues?.length]); // Only run when items are added or removed

    // Render an empty state that includes an Add button
    const renderEmptyState = () => {
        return (
            <TableRow>
                <TableCell colSpan={2} align="center">
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 2
                    }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {emptyText}
                        </Typography>
                        {formikValues.length < maxItems && (
                            <Button
                                startIcon={<AddIcon />}
                                onClick={handleAddItem}
                                variant="outlined"
                                size="small"
                            >
                                {addButtonText}
                            </Button>
                        )}
                    </Box>
                </TableCell>
            </TableRow>
        );
    };

    const totals = calculateTotal ? calculateTotal(rows) : null;

    // For dynamic tables
    return (
        <Box className="dynamic-repeater">
            <Paper variant="outlined" className={className} {...paperProps}>
                <Table {...tableProps}>
                    {headerColumns.length > 0 && (
                        <TableHead>
                            <TableRow>
                                {headerColumns.map((column, idx) => (
                                    <TableCell key={idx}>{column}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                    )}
                    <TableBody>
                        {formikValues.length > 0 ? (
                            formikValues.map((item, index) => (
                                <React.Fragment key={index}>
                                    {createItemRows(item, index)}
                                    {showTotals && totals && (
                                        <TableRow>
                                            <TableCell className="total">{totals.description || "TOTAL"}</TableCell>
                                            <TableCell className="total">{totals.amount}</TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            ))
                        ) : (
                            renderEmptyState()
                        )}
                    </TableBody>
                </Table>
                {subComponent}
            </Paper>
        </Box>
    );

    // Function to render a static table with headers and totals
    function renderStaticTable() {
        // Calculate totals if needed
        const totals = calculateTotal ? calculateTotal(rows) : null;

        return (
            <Box className="static-table">
                {title && (
                    <Typography variant="subtitle1" gutterBottom>
                        {title}
                    </Typography>
                )}

                <Paper variant="outlined" className={className} {...paperProps}>
                    <Table {...tableProps}>
                        {headerColumns.length > 0 && (
                            <TableHead>
                                <TableRow>
                                    {headerColumns.map((column, idx) => (
                                        <TableCell key={idx}>{column}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                        )}
                        <TableBody>
                            {rows.map((row, index) => (
                                <TableRow key={index}>
                                    <TableCell className="label">{row.description}</TableCell>
                                    <TableCell>{row.amount}</TableCell>
                                </TableRow>
                            ))}

                            {showTotals && totals && (
                                <TableRow>
                                    <TableCell className="total">{totals.description || "TOTAL"}</TableCell>
                                    <TableCell className="total">{totals.amount}</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    {subComponent}
                </Paper>
            </Box>
        );
    }
};

export { Fields };